import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { SnackbarProvider } from 'notistack';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Route, Routes } from 'react-router-dom';

import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';

import Login from './authentication/Login';
import NewProject from './project/NewProject';
import { ProjectForm, ScrollToTopOnce } from './project/Project';

import { ThemeProvider } from '@mui/material';
import ActionsPage from './actions/page/ActionsPage';
import './App.css';
import AuthenticationGuard from './authentication/AuthenticationGuard';
import PosthogIdentitySync from './authentication/PosthogIdentitySync';
import { AuthenticationProvider } from './authentication/context/useAuthentication';
import Home from './authentication/Home';
import Recover from './authentication/Recover';
import Register from './authentication/Register';
import ResetPassword from './authentication/ResetPassword';
import Dashboard from './dashboard';
import ProjectTypeList from './dashboard/ProjectTypeList';
import AppLayout from './layout/AppLayout';
import { HeaderProject, HeaderProjects } from './layout/Header';
import { Provider as BackToProvider } from './project/context/useBackTo';
import { ValidationProvider } from './project/context/useValidation';

import AnnualReports from './annual-reports';
import HeaderAnnualReports from './annual-reports/Header';
import AnnualReport from './annual-reports/Report';
import AnnualReportProjectResults from './annual-reports/ReportProjectResults';
import Faq from './faq/Faq';

import Results from './results/Results';
import theme from './theme';
import Pdf from './project/pdf/Pdf';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if ((error as any).message === 'Unauthorized') {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [hasCookieConsent, setHasCookieConsent] = useState(
    getCookieConsentValue() === 'true',
  );

  useEffect(() => {
    if (hasCookieConsent) {
      posthog.opt_in_capturing();
      initSentry();
      return;
    }

    posthog.opt_out_capturing();
    posthog.reset();
  }, [hasCookieConsent]);

  const initSentry = () => {
    if (process.env.REACT_APP_SENTRY_DSN) {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [new BrowserTracing()],
        tracesSampleRate: 1.0,
      });
    }
  };
  const handleAccept = () => {
    setHasCookieConsent(true);
  };

  const handleDecline = () => {
    setHasCookieConsent(false);
  };
  return (
    <ThemeProvider theme={theme}>
      <CookieConsent
        onAccept={handleAccept}
        onDecline={handleDecline}
        flipButtons
        enableDeclineButton
        location="bottom"
        buttonText="Autoriser et fermer"
        declineButtonText="Refuser"
        expires={150}
      >
        SONAR ne fait <b>aucun suivi publicitaire</b> et ne collecte
        aucune données personnelles, hors données d'authentification. Des
        cookies sont utilisés à des fins statistiques ou de fonctionnement,
        ainsi que d'analyse (que vous pouvez refuser ici), nous permettant
        d'améliorer le site en continu.
      </CookieConsent>
      <QueryClientProvider client={queryClient}>
        <BackToProvider>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            style={{ top: '64px' }}
            className="snackbar"
          >
            <AuthenticationProvider>
              {hasCookieConsent ? <PosthogIdentitySync /> : null}
              <Routes>
                <Route path="/authentication" element={<Home />} />
                <Route path="/authentication/login" element={<Login />} />
                <Route path="/authentication/register" element={<Register />} />
                <Route path="/authentication/recover" element={<Recover />} />
                <Route
                  path="/authentication/recover/:email"
                  element={<Recover />}
                />
                <Route
                  path="/authentication/reset-password/:token"
                  element={
                    <ResetPassword
                      title="Réinitialisation mot de passe"
                      fieldLabel="Nouveau mot de passe"
                      successMessage="Mot de passe réinitialisé avec succès"
                      buttonLabel="Réinitialiser"
                    />
                  }
                />
                <Route
                  path="/authentication/confirm-account/:token"
                  element={
                    <ResetPassword
                      title="Création de compte"
                      fieldLabel="Mot de passe"
                      successMessage="Compte créé avec succès"
                      buttonLabel="Créer"
                    />
                  }
                />

                <Route
                  path="/"
                  element={
                    <AuthenticationGuard>
                      <Dashboard />
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/projects/:type/"
                  element={
                    <AuthenticationGuard>
                      <ProjectTypeList />
                    </AuthenticationGuard>
                  }
                />

                {/* ANNUAL REPORTS */}

                <Route
                  path="/annual-reports"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderAnnualReports />}>
                        <ScrollToTopOnce />
                        <AnnualReports />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/annual-reports/:reportId"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderAnnualReports />}>
                        <ScrollToTopOnce />
                        <AnnualReport />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/annual-reports/:reportId/:projectId"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderAnnualReports />}>
                        <ScrollToTopOnce />
                        <AnnualReportProjectResults />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/project"
                  element={
                    <AuthenticationGuard>
                      <NewProject />
                    </AuthenticationGuard>
                  }
                />

                <Route
                  path="/project/:projectId/"
                  element={
                    <AuthenticationGuard>
                      <ValidationProvider>
                        <AppLayout header={<HeaderProject />}>
                          <ProjectForm />
                        </AppLayout>
                      </ValidationProvider>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/project/:projectId/results"
                  element={
                    <AuthenticationGuard>
                      <ValidationProvider>
                        <AppLayout header={<HeaderProject />}>
                          <ScrollToTopOnce />
                          <Results />
                        </AppLayout>
                      </ValidationProvider>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/project/:projectId/actions"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderProject />}>
                        <ScrollToTopOnce />
                        <ActionsPage />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/faq"
                  element={
                    <AuthenticationGuard>
                      <AppLayout header={<HeaderProjects />}>
                        <ScrollToTopOnce />
                        <Faq />
                      </AppLayout>
                    </AuthenticationGuard>
                  }
                />
                <Route
                  path="/project/:projectId/pdf"
                  element={
                    <AuthenticationGuard>
                      <Pdf />
                    </AuthenticationGuard>
                  }
                />
              </Routes>
            </AuthenticationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </SnackbarProvider>
        </BackToProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
